/**
 * Extra WhatsApp-Web sessions (linked devices) of ONE QR number -- list, add, scan again, disconnect, remove, choose which login receives
 * messages. Only used while the account has multi-session switched on (the numbers payload says maxSessions > 1); the server enforces it too.
 * Polls like useWhatsAppQrNumbers: fast while a session is being scanned / connecting (so its QR appears promptly), slow otherwise.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const FAST_MS = 3000;
const SLOW_MS = 15000;
const CONNECTING = new Set(["CREATING", "QR_PENDING", "AUTHENTICATING"]);

async function authRequest(method, path, body) {
  const token = await getAuthToken();
  const res = await axios({
    method,
    url: `${SERVER_DOMAIN}${path}`,
    data: body,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.data;
}

export function useWhatsAppQrSessions(workspaceId, numberId, enabled) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const base = `/api/workspaces/${workspaceId}/whatsapp-qr/numbers/${numberId}`;
  const baseRef = useRef(base);
  baseRef.current = base;

  const refresh = useCallback(async () => {
    if (!workspaceId || !numberId) return null;
    const next = await authRequest("get", `${baseRef.current}/sessions`);
    setData(next);
    return next;
  }, [workspaceId, numberId]);

  useEffect(() => {
    if (!enabled || !workspaceId || !numberId) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async (first) => {
      let next = SLOW_MS;
      try {
        const res = await refresh();
        if ((res?.sessions || []).some((s) => CONNECTING.has(s.state))) next = FAST_MS;
        if (!cancelled) setError(null);
      } catch (err) {
        if (!cancelled && first) setError(err?.response?.data?.message || err.message);
      } finally {
        if (first && !cancelled) setLoading(false);
      }
      if (!cancelled) timer = setTimeout(() => tick(false), next);
    };
    setLoading(true);
    tick(true);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, workspaceId, numberId, refresh]);

  // Each action rethrows after recording the message, so a caller can also react (e.g. keep a form open).
  const run = useCallback(
    async (fn) => {
      setError(null);
      try {
        const out = await fn();
        await refresh().catch(() => {});
        return out;
      } catch (err) {
        setError(err?.response?.data?.message || err.message);
        throw err;
      }
    },
    [refresh]
  );

  const addSession = useCallback((label) => run(() => authRequest("post", `${baseRef.current}/sessions`, { label })), [run]);
  const connectSession = useCallback((id) => run(() => authRequest("post", `${baseRef.current}/sessions/${id}/connect`, {})), [run]);
  const logoutSession = useCallback((id) => run(() => authRequest("post", `${baseRef.current}/sessions/${id}/logout`, {})), [run]);
  const removeSession = useCallback((id) => run(() => authRequest("delete", `${baseRef.current}/sessions/${id}`)), [run]);
  const renameSession = useCallback((id, label) => run(() => authRequest("patch", `${baseRef.current}/sessions/${id}`, { label })), [run]);
  // sessionId: a session's id, or "main" for the number's own login.
  const setReceiver = useCallback((sessionId) => run(() => authRequest("put", `${baseRef.current}/receiver`, { sessionId })), [run]);

  return { data, loading, error, refresh, addSession, connectSession, logoutSession, removeSession, renameSession, setReceiver };
}

export default useWhatsAppQrSessions;
